import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import React, { useEffect, useState } from 'react'

import { type ProspectEmployee } from './types'
import { formatMoney } from '../../utils'
import { useGetProspectEmployees } from '../hooks/useGetProspectEmployees'
import TanstackTable, {
  DEFAULT_PAGE_SIZE,
  type PaginationMetadata,
} from '../reusable/table/TanstackTable'

const columnHelper = createColumnHelper<ProspectEmployee>()
const columns = [
  columnHelper.accessor(
    (row) => ({
      content: (
        <div>
          <div>{row.name}</div>
          <div className='caption text-gray-500'>
            {row.id}
          </div>
        </div>
      ),
    }),
    {
      header: 'Employee Name and ID',
      id: 'employee_name_and_id',
      cell: (info) => info.getValue().content,
    }
  ),
  columnHelper.accessor('date_of_birth', {
    header: 'Date of birth',
  }),
  columnHelper.accessor('zip', {
    header: 'Zip Code',
  }),
  columnHelper.accessor('dependents', {
    header: 'Number of Dependents',
    cell: (info) => {
      return info.getValue()?.length || 0
    },
  }),
  columnHelper.accessor('employee_medical_contribution', {
    header: 'Medical Contributions',
    cell: (info) => {
      return info.getValue() ? formatMoney(info.getValue()!) : '-'
    },
  }),
  columnHelper.accessor('yearly_pay', {
    header: 'Annual Pay',
    cell: (info) => {
      return info.getValue() ? formatMoney(info.getValue()!) : '-'
    },
  }),
]

export function ProspectEmployeeList ({
  prospectId,
  censusId,
}: {
  prospectId: string;
  censusId: string;
}) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()
  const [pagination, setPagination] = useState({
    pageIndex: 1,
    pageSize: DEFAULT_PAGE_SIZE,
  })

  const { data, isLoading, isError } = useGetProspectEmployees({
    prospectId,
    censusId,
    pagination,
  })

  const table = useReactTable({
    data: data?.data || [],
    columns,
    manualPagination: true,
    getCoreRowModel: getCoreRowModel(),
    state: {
      pagination,
    },
  })

  useEffect(() => {
    if (data) {
      setPaginationMetadata(data?.pagination)
    }
  }, [data?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no employees for this census.'
      paginationLabelSingular='employee'
      paginationMetadata={paginationMetadata}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}
